import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  ChevronDown,
  ShieldCheck,
  MapPin,
  Ambulance,
  ChevronRight,
  Stethoscope,
  Pill,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import BottomNav from '@/components/BottomNav';
import ResqlyServiceCard from '@/components/resqly/ResqlyServiceCard';
import ResqlyCard from '@/components/resqly/ResqlyCard';
import ResqlyStatus from '@/components/resqly/ResqlyStatus';
import RangerEntrySection from '@/components/resqly/RangerEntrySection';
import { bookingStatusLabel } from '@/lib/bookingHistory';
import { CONSUMER_SERVICES } from '@/lib/constants';
import useConsumerLocationSync from '@/hooks/useConsumerLocationSync';

const SERVICE_HINTS = {
  ambulance: 'Urgent medical transport',
  doctor: 'Consult a verified doctor',
  pharmacy: 'Medicines delivered to you',
  home_nursing: 'Skilled nursing at home',
  home_care: 'Daily care and support',
  lab_test: 'Tests and reports at home',
  bystander: 'On-scene assistance',
  pet_care: 'Veterinary care for pets',
};

const CORE_KEYS = ['doctor', 'pharmacy', 'home_nursing', 'lab_test'];
const SUPPORT_KEYS = ['home_care', 'bystander', 'pet_care'];
const ACTIVE_STATE_POLL_MS = 3000;

function greetingFor(name) {
  const h = new Date().getHours();
  const greet = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  const first = (name || '').trim().split(/\s+/)[0];
  return { greet, hey: first ? `Hey ${first}` : 'Welcome' };
}

function enrichService(s) {
  return { ...s, hint: SERVICE_HINTS[s.key] };
}

const SERVICE_LABELS = Object.fromEntries(CONSUMER_SERVICES.map((s) => [s.key, s.label]));
function serviceLabelFor(key) {
  return SERVICE_LABELS[key] || (key || '');
}

export default function ConsumerHome() {
  const { me } = useAuth();
  const liveLocation = useConsumerLocationSync(me);
  const navigate = useNavigate();
  const [activeBooking, setActiveBooking] = useState(null);
  const [activeRequest, setActiveRequest] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showAllServices, setShowAllServices] = useState(false);

  const fetchCurrent = () => {
    return Promise.all([
      api
        .get('/consumers/me/bookings?section=active&page=1&page_size=1')
        .then(({ data }) => data.bookings?.[0] || null)
        .catch(() => null),
      api
        .get('/service-requests/me/active')
        .then(({ data }) => data.request || null)
        .catch(() => null),
    ]).then(([booking, request]) => {
      setActiveBooking(booking);
      setActiveRequest(request);
    });
  };

  useEffect(() => {
    fetchCurrent();
    const timer = setInterval(fetchCurrent, ACTIVE_STATE_POLL_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    api
      .get('/notifications')
      .then(({ data }) => {
        const unread = (data.notifications || []).filter((n) => !n.read).length;
        setUnreadCount(unread);
      })
      .catch(() => {});
  }, []);

  // Re-fetch current booking/request when the page regains visibility so that
  // a cancellation/completion on another screen is immediately reflected on
  // Home without requiring an app restart.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchCurrent();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const locationLabel = liveLocation.label || me?.location || me?.city || (liveLocation.locating ? 'Locating you…' : 'Set your location');
  const coreServices = CONSUMER_SERVICES.filter((s) => CORE_KEYS.includes(s.key)).map(enrichService);
  const supportServices = CONSUMER_SERVICES.filter((s) => SUPPORT_KEYS.includes(s.key)).map(enrichService);
  const visibleSupport = showAllServices ? supportServices : supportServices.slice(0, 2);

  return (
    <div className="resqly-shell">
      <div className="resqly-frame flex flex-col min-h-screen min-h-[100dvh]">
        <header
          className="px-5 pt-[max(3.5rem,env(safe-area-inset-top)+2rem)] pb-3 flex items-center justify-between gap-3"
        >
          <button
            data-testid="home-location"
            onClick={() => navigate('/consumer/location')}
            className="flex items-start gap-2 text-left min-w-0 flex-1"
          >
            <MapPin className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                Your location
              </div>
              <div className="text-sm font-semibold text-slate-900 flex items-center gap-1 truncate">
                <span className="truncate">{locationLabel}</span>
                <ChevronDown className="w-3 h-3 flex-shrink-0" />
              </div>
            </div>
          </button>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              data-testid="home-notifications"
              onClick={() => navigate('/consumer/notifications')}
              className="p-2.5 bg-white rounded-full shadow-sm border border-[var(--resqly-border)] relative"
            >
              <Bell className="w-5 h-5 text-slate-700" />
              {unreadCount > 0 && (
                <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full" />
              )}
            </button>
            <button
              data-testid="home-sos-vault"
              onClick={() => navigate('/consumer/sos-vault')}
              className="px-3 py-2 bg-rose-50 text-rose-700 rounded-full inline-flex items-center gap-1.5 font-semibold text-xs border border-rose-100"
            >
              <ShieldCheck className="w-4 h-4" />
              <span>SOS Vault</span>
            </button>
          </div>
        </header>

        <div className="px-5 pb-2" data-testid="home-greeting">
          <div className="text-sm font-medium text-slate-500" data-testid="home-greeting-time">
            {greetingFor(me?.name).greet}
          </div>
          <div className="text-2xl font-bold text-slate-900 tracking-tight mt-0.5" data-testid="home-greeting-name">
            {greetingFor(me?.name).hey}
          </div>
          <p className="text-sm text-slate-500 mt-1">What do you need right now?</p>
        </div>

        {activeBooking && (
          <div className="px-5 mt-4">
            <ResqlyCard
              className="p-4 bg-gradient-to-br from-blue-50 to-white border-blue-100"
              elevated
              data-testid="home-active-booking"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <ResqlyStatus tone="primary" dot>
                    Active booking
                  </ResqlyStatus>
                  <div className="font-semibold text-slate-900 mt-2 truncate">
                    {activeBooking.service_label || activeBooking.service_key}
                  </div>
                  <div className="text-sm text-slate-600 mt-0.5">
                    {bookingStatusLabel(activeBooking)}
                  </div>
                </div>
                <button
                  type="button"
                  className="text-sm font-semibold text-blue-700 whitespace-nowrap"
                  onClick={() => {
                    if (activeBooking.actions?.can_track) {
                      navigate(`/consumer/bookings/${activeBooking.id}/tracking`);
                    } else {
                      navigate(`/consumer/bookings/${activeBooking.id}`);
                    }
                  }}
                >
                  View
                </button>
              </div>
            </ResqlyCard>
          </div>
        )}

        {!activeBooking && activeRequest && activeRequest.id && (
          <div className="px-5 mt-4">
            <ResqlyCard
              className="p-4 bg-gradient-to-br from-indigo-50 to-white border-indigo-100"
              elevated
              data-testid="home-active-request"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <ResqlyStatus tone="primary" dot>
                    Active request
                  </ResqlyStatus>
                  <div className="font-semibold text-slate-900 mt-2 truncate">
                    {activeRequest.service_label || serviceLabelFor(activeRequest.service_key) || 'Searching for a responder'}
                  </div>
                  <div className="text-sm text-slate-600 mt-0.5">
                    {activeRequest.broadcast_active ? 'Finding verified responders near you…' : 'Request in progress'}
                  </div>
                </div>
                <button
                  type="button"
                  className="text-sm font-semibold text-blue-700 whitespace-nowrap"
                  onClick={() => navigate(`/consumer/broadcast/${activeRequest.id}`)}
                >
                  View
                </button>
              </div>
            </ResqlyCard>
          </div>
        )}

        <div className="px-5 mt-4">
          <button
            data-testid="home-emergency-sos"
            onClick={() => navigate('/consumer/emergency-request')}
            className="emergency-fab w-full rounded-[var(--resqly-radius-xl)] p-4 text-white text-left flex items-center gap-3 active:scale-[0.98] transition-transform"
          >
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center relative flex-shrink-0">
              <Ambulance className="w-6 h-6 text-white" strokeWidth={2.4} />
              <span className="emergency-pulse absolute inset-0 rounded-2xl" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded-full">
                  Emergency
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-white/80">
                  24/7
                </span>
              </div>
              <div className="text-base font-bold mt-1">Request ambulance</div>
              <div className="text-xs text-white/85">Nearest verified ambulance — tap to start</div>
            </div>
            <ChevronRight className="w-5 h-5 text-white flex-shrink-0" />
          </button>
        </div>

        <section className="px-5 mt-6">
          <div className="flex items-center gap-2 mb-3">
            <Stethoscope className="w-4 h-4 text-blue-700" />
            <h2 className="font-semibold text-slate-900">Core healthcare</h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {coreServices.map((s) => (
              <ResqlyServiceCard
                key={s.key}
                service={s}
                to={`/consumer/service/${s.key}`}
                compact
              />
            ))}
          </div>
        </section>

        <section className="px-5 mt-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Pill className="w-4 h-4 text-slate-500" />
              <h2 className="font-semibold text-slate-900">More services</h2>
            </div>
            {!showAllServices && supportServices.length > 2 && (
              <button
                type="button"
                onClick={() => setShowAllServices(true)}
                className="text-xs font-semibold text-blue-700"
              >
                See all
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {visibleSupport.map((s) => (
              <ResqlyServiceCard
                key={s.key}
                service={s}
                to={`/consumer/service/${s.key}`}
                compact
              />
            ))}
          </div>
        </section>

        <RangerEntrySection />

        <div className="flex-1 min-h-4" />
        <BottomNav variant="consumer" />
      </div>
    </div>
  );
}
